import React, { type FC } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  MessagesSquareIcon,
  ListIcon,
  SendIcon,
  ArrowRightIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { showInConciseMode } from '../chat/threadSettings';
import {
  STATUS_LABEL,
  TYPE_LABEL,
  type RequestStatus,
  type RequestType,
} from '../../services/supportRequest.service';
import { StatusBadge, RelativeTime } from '../../pages/requests/requestUi';
import { cn } from '@/lib/utils';

/**
 * Ellie's four request tools, rendered as one card.
 *
 * The tools speak about the same object the Requests section shows, so the card
 * borrows that section's own StatusBadge and RelativeTime rather than inventing
 * a second visual language for a status the user has already learned to read
 * elsewhere.
 *
 * Two exports, one implementation: `showInConciseMode` is a flag set on the
 * component itself, and these tools don't agree about it. Opening a request or
 * posting a reply is an action taken on the user's behalf and has to stay
 * visible even at low verbosity — it's the only place the user learns it
 * happened. Listing and reading are lookups Ellie does to answer a question,
 * and the answer she writes covers them, so they hide with everything else.
 */

interface RequestToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseArgs(args: any, argsText: string): Record<string, any> {
  try {
    return typeof args === 'object' && args ? args : JSON.parse(argsText);
  } catch {
    return {};
  }
}

function parseResult(result?: string): { success?: boolean; error?: string; data: any } {
  if (result === undefined) return { data: {} };
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return { success: parsed?.success, error: parsed?.error, data: parsed };
  } catch {
    return { data: {} };
  }
}

/** Status and type come back as raw strings, so neither lookup can be trusted. */
function isKnownStatus(status?: string): status is RequestStatus {
  return !!status && status in STATUS_LABEL;
}

function typeLabel(requestType?: string): string | null {
  if (!requestType) return null;
  return TYPE_LABEL[requestType as RequestType] ?? requestType.replace(/_/g, ' ');
}

const TOOL_CONFIG: Record<
  string,
  {
    icon: FC<{ className?: string }>;
    /** Heading while the tool is in flight. */
    runningLabel: (args: Record<string, any>) => string;
    /** The muted "what this was" line, shown once it's done or failed. */
    completedLabel: (args: Record<string, any>) => string;
    errorLabel: string;
  }
> = {
  create_support_request: {
    icon: MessagesSquareIcon,
    runningLabel: (a) =>
      a.title ? `Opening a request: "${a.title}"...` : 'Opening a request with the Pliable team...',
    completedLabel: () => 'Request with the Pliable team',
    errorLabel: 'Could not open the request',
  },
  list_my_requests: {
    icon: ListIcon,
    runningLabel: (a) =>
      a.query ? `Checking your requests for "${a.query}"...` : 'Checking your open requests...',
    completedLabel: () => 'Your open requests',
    errorLabel: 'Could not load your requests',
  },
  get_request: {
    icon: MessagesSquareIcon,
    runningLabel: () => 'Reading the request...',
    completedLabel: () => 'Request',
    errorLabel: 'Could not read the request',
  },
  reply_to_request: {
    icon: SendIcon,
    runningLabel: () => 'Posting your reply to the request...',
    completedLabel: () => 'Reply to a request',
    errorLabel: 'Could not post the reply',
  },
};

/** A request summary as `_summarize` in request_tools.py returns it. */
interface RequestSummary {
  id?: string;
  title?: string;
  request_type?: string;
  status?: string;
  last_message_at?: string | null;
  last_message_preview?: string;
  message_count?: number;
  created_at?: string | null;
}

function RequestLine({ request, showPreview = true }: { request: RequestSummary; showPreview?: boolean }) {
  const label = typeLabel(request.request_type);

  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-foreground">{request.title || 'Untitled request'}</span>
        {isKnownStatus(request.status) && <StatusBadge status={request.status} />}
        {label && (
          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
            {label}
          </Badge>
        )}
        {!!request.message_count && (
          <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
            <MessagesSquareIcon className="h-3 w-3" />
            {request.message_count}
          </span>
        )}
        {request.last_message_at && (
          <span className="text-[11px] text-muted-foreground">
            <RelativeTime iso={request.last_message_at} />
          </span>
        )}
      </div>
      {showPreview && request.last_message_preview && (
        <p className="text-[11px] text-muted-foreground m-0 line-clamp-2">
          {request.last_message_preview}
        </p>
      )}
    </div>
  );
}

/** Ellie only ever runs these in-app, so the Requests route is always reachable. */
function ViewRequestLink({ requestId }: { requestId?: string }) {
  if (!requestId) return null;
  return (
    <Link
      to={`/requests/${requestId}`}
      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      View request
      <ArrowRightIcon className="h-3 w-3" />
    </Link>
  );
}

function ToolBody({
  toolName,
  parsedArgs,
  data,
}: {
  toolName: string;
  parsedArgs: Record<string, any>;
  data: any;
}) {
  if (toolName === 'create_support_request') {
    const request: RequestSummary = data?.request ?? {};
    return (
      <div className="ml-6 mt-1 space-y-1.5">
        <RequestLine request={{ ...request, title: request.title || parsedArgs.title }} showPreview={false} />
        {parsedArgs.description && (
          <p className="text-[11px] text-muted-foreground m-0 line-clamp-3">{parsedArgs.description}</p>
        )}
        <ViewRequestLink requestId={request.id} />
      </div>
    );
  }

  if (toolName === 'list_my_requests') {
    const requests: RequestSummary[] = data?.requests ?? [];
    if (requests.length === 0) return null;
    return (
      <div className="ml-6 mt-1 space-y-2">
        {requests.map((req, i) => (
          <RequestLine key={req.id || i} request={req} />
        ))}
      </div>
    );
  }

  if (toolName === 'get_request') {
    const request: RequestSummary = data?.request ?? {};
    const messages: any[] = data?.messages ?? [];
    return (
      <div className="ml-6 mt-1 space-y-1.5">
        <RequestLine request={request} showPreview={false} />
        {messages.length > 0 && (
          <p className="text-[11px] text-muted-foreground m-0">
            {messages.length} message{messages.length !== 1 ? 's' : ''} in the thread
          </p>
        )}
        <ViewRequestLink requestId={request.id} />
      </div>
    );
  }

  if (toolName === 'reply_to_request') {
    if (!parsedArgs.body) return null;
    return (
      <div className="ml-6 mt-1 space-y-1.5">
        <p className="text-[11px] text-muted-foreground m-0 border-l-2 border-border pl-2 line-clamp-4">
          {parsedArgs.body}
        </p>
        <ViewRequestLink requestId={parsedArgs.request_id} />
      </div>
    );
  }

  return null;
}

/** The green line: what actually happened, in the user's terms. */
function resultLabel(toolName: string, data: any): string {
  if (toolName === 'create_support_request') {
    return 'Opened — the Pliable team has been notified.';
  }
  if (toolName === 'list_my_requests') {
    const total = data?.requests?.length ?? 0;
    return total === 0
      ? 'Nothing open right now.'
      : `Found ${total} open request${total !== 1 ? 's' : ''}`;
  }
  if (toolName === 'get_request') {
    return 'Loaded the conversation.';
  }
  return 'Reply posted to the request.';
}

const RequestToolCallComponent: FC<RequestToolCallProps> = ({
  toolName,
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  // Derived from `result` rather than from status.type === 'complete': a thread
  // loaded from history can arrive with no status at all, and keying on the
  // literal would leave a finished call spinning forever.
  const isComplete = !isRunning && !isError && result !== undefined;

  const parsedArgs = parseArgs(args, argsText);
  const { success, error: resultError, data } = parseResult(result);
  // list_my_requests has no `success` key at all, so only an explicit false counts.
  const hasError = isError || (result !== undefined && success === false) || !!resultError;

  const config = TOOL_CONFIG[toolName] || TOOL_CONFIG.create_support_request;
  const Icon = config.icon;

  return (
    <Card>
      <div
        className={cn(
          'p-3',
          isRunning
            ? 'bg-primary/10 border-b border-primary/20'
            : hasError
              ? 'bg-destructive/10'
              : 'bg-muted/50',
        )}
      >
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{config.runningLabel(parsedArgs)}</p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{config.completedLabel(parsedArgs)}</p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">{resultError || config.errorLabel}</p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{config.completedLabel(parsedArgs)}</p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">{resultLabel(toolName, data)}</p>
            </div>
            <ToolBody toolName={toolName} parsedArgs={parsedArgs} data={data} />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{config.runningLabel(parsedArgs)}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

/** create_support_request / reply_to_request — actions, so never hidden. */
export const RequestToolCall = RequestToolCallComponent as React.ComponentType<any>;
showInConciseMode(RequestToolCall);

/**
 * list_my_requests / get_request — lookups. A separate component identity so it
 * carries no showInConciseMode flag; the rendering is identical.
 */
export const RequestLookupToolCall = ((props: RequestToolCallProps) => (
  <RequestToolCallComponent {...props} />
)) as React.ComponentType<any>;
